import React, { useState, useEffect } from "react";
import { usePaginated, useAsync } from "../hooks";
import { api } from "../api";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Spinner,
  ApiError,
  Modal,
  Pages,
  Inp,
  Sel,
  Txa,
  Empty,
} from "../ui";
import ReceiptPrintView from "./ReceiptPrintView";
import PaymentInfoPicker from "../PaymentInfoPicker";
import ClientTripSelect, { isoDay } from "../ClientTripSelect";

import { BRAND } from "../brand";

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const RCT_STATUSES = ["all", "issued", "cancelled", "refunded"];
const RCT_STATUS_COLORS = {
  issued: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-400",
  refunded: "bg-amber-100 text-amber-700",
};

// ─── PAYMENT METHOD OPTIONS ───────────────────────────────────────────────────
const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card", label: "Card Payment" },
  { value: "cash", label: "Cash" },
  { value: "paystack", label: "Paystack" },
  { value: "crypto", label: "Cryptocurrency" },
  { value: "other", label: "Other" },
];

// ─── CURRENCY OPTIONS ─────────────────────────────────────────────────────────
const CURRENCIES = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "GHS", label: "GHS – Ghanaian Cedi" },
  { value: "KES", label: "KES – Kenyan Shilling" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
];

// ─── COUNTRY OPTIONS ──────────────────────────────────────────────────────────
const COUNTRIES = [
  { value: "", label: "Select country (optional)" },
  { value: "Nigeria", label: "🇳🇬 Nigeria" },
  { value: "Canada", label: "🇨🇦 Canada" },
];

// ─── SUMMARY CURRENCIES — only USD + NGN on dashboard ────────────────────────
const SUMMARY_CURRENCIES = ["USD", "NGN"];

const fmtMoney = (n, cur = "USD") =>
  n != null
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: cur,
        minimumFractionDigits: 2,
      }).format(n)
    : "–";

const fmtD = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ─── DATE INPUT ───────────────────────────────────────────────────────────────
function DateInp({ label, value, onChange, min, required, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
          {label}
          {required && " *"}
        </label>
      )}
      <input
        type="date"
        value={value || ""}
        onChange={onChange}
        min={min}
        required={required}
        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
      />
    </div>
  );
}

// ─── RECEIPT ITEM EDITOR ──────────────────────────────────────────────────────
function ItemsEditor({ items, onChange, currency = "USD" }) {
  const addRow = () =>
    onChange([...items, { description: "", quantity: 1, unitPrice: 0 }]);
  const removeRow = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) =>
    onChange(
      items.map((row, idx) =>
        idx === i ? { ...row, [k]: k === "description" ? v : +v || 0 } : row,
      ),
    );
  const subtotal = items.reduce((s, r) => s + r.quantity * r.unitPrice, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
          Line Items *
        </label>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-lg font-semibold">
          {currency}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((row, i) => (
          <div
            key={i}
            className="border border-slate-200 rounded-xl p-3 bg-white hover:border-slate-300 transition-colors group"
          >
            <div className="mb-3">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">
                Description
              </label>
              <textarea
                rows={3}
                placeholder={`e.g. Dubai Return Flight (Emirates) – 2 Adults\nDeparture: 15 Jun 2025 · Return: 30 Jun 2025\nIncludes checked baggage`}
                value={row.description}
                onChange={(e) => setRow(i, "description", e.target.value)}
                required
                className="w-full px-3 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] resize-y transition-colors placeholder:text-slate-300 leading-relaxed"
              />
            </div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">
                  Qty
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-2.5 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-center"
                  value={row.quantity}
                  onChange={(e) => setRow(i, "quantity", e.target.value)}
                />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">
                  Unit Price ({currency})
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full px-2.5 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right"
                  value={row.unitPrice}
                  onChange={(e) => setRow(i, "unitPrice", e.target.value)}
                />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">
                  Line Total
                </label>
                <div className="w-full px-2.5 py-2 text-sm font-bold text-slate-700 rounded-lg bg-slate-50 border border-slate-200 text-right">
                  {fmtMoney(row.quantity * row.unitPrice, currency)}
                </div>
              </div>
              <div className="col-span-1 flex justify-center pb-1">
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={addRow}
          className="text-xs font-semibold text-[rgb(var(--brand-rgb))] hover:text-[rgb(var(--brand-dark-rgb))] flex items-center gap-1.5 transition-colors px-3 py-2 rounded-lg hover:bg-[rgb(var(--brand-rgb)/0.05)]"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          Add Line Item
        </button>
        <div className="text-right">
          <p className="text-xs text-slate-400 mb-0.5">Subtotal</p>
          <p className="text-base font-bold text-slate-800">
            {fmtMoney(subtotal, currency)}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENT RECEIPT HISTORY ───────────────────────────────────────────────────
// Fetches previous receipts for the selected client and pre-calculates payment totals
function ClientReceiptHistory({ clientId, currency, onPreFill }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setHistory(null);
      return;
    }
    setLoading(true);
    api
      .get(`/crm/receipts?client=${clientId}&limit=50`)
      .then((res) => setHistory(res.data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (!clientId) return null;

  if (loading)
    return (
      <div className="text-xs text-slate-400 py-2 flex items-center gap-2">
        <svg
          className="animate-spin"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
        Loading client history…
      </div>
    );

  if (!history || !history.length)
    return (
      <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
        No previous receipts found for this client.
      </div>
    );

  // Filter to same currency + issued status for meaningful pre-calculation
  // const sameCur    = history.filter(r => r.currency === currency && r.status === "issued");
if (!history || !history.length) return (
  <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
    No previous receipts found for this client.
  </div>
);

const sameCur = history.filter(r => r.currency === currency);

// ✅ latest receipt (already sorted from backend)
const latestReceipt = sameCur[0];

// ✅ totalCost = latest receipt ONLY
const totalCost = latestReceipt?.totalCost ?? latestReceipt?.total ?? 0;

// ✅ totalPaid = sum of ALL receipt items
const totalPaid = sameCur.reduce((sum, receipt) => {
  const itemsTotal = (receipt.items || []).reduce(
    (s, item) => s + item.quantity * item.unitPrice,
    0
  );
  return sum + itemsTotal;
}, 0);

// ✅ pending = totalCost - totalPaid
const pending = Math.max(totalCost - totalPaid, 0);

  return (
    <div className="space-y-3">
      {/* Pre-calculated summary */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-3">
          Calculated from {sameCur.length} issued receipt
          {sameCur.length !== 1 ? "s" : ""} ({currency})
        </p>
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Total Trip Cost</p>
            <p className="font-bold text-slate-800">
              {fmtMoney(totalCost, currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Total Paid</p>
            <p className="font-bold text-emerald-600">
              {fmtMoney(totalPaid, currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Pending Balance</p>
            <p
              className={`font-bold ${pending > 0 ? "text-amber-600" : "text-emerald-600"}`}
            >
              {fmtMoney(pending, currency)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            onPreFill({ totalCost, totalPaid, pendingBalance: pending })
          }
          className="text-xs font-semibold text-blue-600 hover:text-blue-700 bg-white border border-blue-200 rounded-lg px-3 py-1.5 transition-colors hover:bg-blue-50"
        >
          ↓ Pre-fill payment fields with these values
        </button>
      </div>

      {/* History rows */}
      <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
        {history.slice(0, 10).map((r) => (
          <div
            key={r._id}
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-xs"
          >
            <div className="min-w-0">
              <span className="font-bold text-slate-700">
                {r.receiptReference}
              </span>
              {r.destination && (
                <span className="text-slate-400 ml-2">{r.destination}</span>
              )}
              {r.paymentDate && (
                <span className="text-slate-400 ml-2">
                  {fmtD(r.paymentDate)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`px-2 py-0.5 rounded-full font-semibold capitalize ${RCT_STATUS_COLORS[r.status] || "bg-slate-100 text-slate-400"}`}
              >
                {r.status}
              </span>
              <span className="font-semibold text-slate-700">
                {fmtMoney(r.amountPaid ?? r.total, r.currency)}
              </span>
              {(r.pendingBalance || 0) > 0 && (
                <span className="text-amber-600 font-semibold">
                  bal: {fmtMoney(r.pendingBalance, r.currency)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── RECEIPT FORM (create / edit) ─────────────────────────────────────────────
function ReceiptForm({
  initial = {},
  clients = [],
  invoices = [],
  onSubmit,
  onClose,
  saving,
}) {
  const [f, sf] = useState({
    receiptReference: initial.receiptReference || "",
    invoice: initial.invoice?._id || initial.invoice || "",
    client: initial.client?._id || initial.client || "",
    clientName: initial.clientName || "",
    email: initial.email || "",
    phone: initial.phone || "",
    country: initial.country || "",
    destination: initial.destination || "",
    tripStartDate: initial.tripStartDate || "",
    tripEndDate: initial.tripEndDate || "",
    paymentDate: initial.paymentDate || new Date().toISOString().split("T")[0],
    currency: initial.currency || BRAND.currency,
    paymentMethod: initial.paymentMethod || "bank_transfer",
    transactionReference: initial.transactionReference || "",
    paystackPaymentLink: initial.paystackPaymentLink || "",
    notes: initial.notes || "",
    status: initial.status || "issued",
    tripIndex: "", // which of the client's trips this is for (form-only, not saved)
    // ── Payment flow ─────────────────────────────────────────────────────────
    totalCost: initial.totalCost != null ? String(initial.totalCost) : "",
    amountPaid: initial.amountPaid != null ? String(initial.amountPaid) : "",
    pendingBalance:
      initial.pendingBalance != null ? String(initial.pendingBalance) : "",
    dueDate: initial.dueDate || "",
    bankPaymentInfo: {
      accountNumber: initial.bankPaymentInfo?.accountNumber || "",
      accountName: initial.bankPaymentInfo?.accountName || "",
      bankName: initial.bankPaymentInfo?.bankName || "",
    },
    items: initial.items?.length
      ? initial.items.map((i) => ({
          description: i.description,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
        }))
      : [{ description: "", quantity: 1, unitPrice: 0 }],
  });

  const set = (k) => (e) => sf((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k, v) => sf((p) => ({ ...p, [k]: v }));
  const setB = (k) => (e) =>
    sf((p) => ({
      ...p,
      bankPaymentInfo: { ...p.bankPaymentInfo, [k]: e.target.value },
    }));

  // Auto-recalculate pending balance whenever totalCost or amountPaid changes
  useEffect(() => {
    const cost = parseFloat(f.totalCost) || 0;
    const paid = parseFloat(f.amountPaid) || 0;
    if (cost > 0 || paid > 0) {
      sf((p) => ({
        ...p,
        pendingBalance: String(Math.max(0, cost - paid).toFixed(2)),
      }));
    }
  }, [f.totalCost, f.amountPaid]);

  const itemsSubtotal = f.items.reduce(
    (s, r) => s + r.quantity * r.unitPrice,
    0,
  );

  const selectedClient = (clients || []).find((x) => x._id === f.client);

  // Fill trip details (destination, dates, currency, total cost) from one of the client's trips.
  const applyTrip = (p, c, idx) => {
    const t = c?.trips?.[idx];
    if (!t) return { ...p, tripIndex: "" };
    const total = t.payment?.totalTripCost;
    return {
      ...p,
      tripIndex: String(idx),
      destination: (t.destinations || []).join(", ") || p.destination,
      tripStartDate: isoDay(t.tripStartDate) || p.tripStartDate,
      tripEndDate: isoDay(t.tripEndDate) || p.tripEndDate,
      currency: t.payment?.currency || p.currency,
      totalCost: total != null ? String(total) : p.totalCost,
    };
  };

  const handleClientSelect = (e) => {
    const id = e.target.value;
    const c = (clients || []).find((x) => x._id === id);
    sf((p) =>
      applyTrip(
        {
          ...p,
          client: id,
          clientName: c?.fullName || p.clientName,
          email: c?.email || p.email,
          phone: c?.phone || p.phone,
          country: c?.countryOfResidence || p.country,
        },
        c,
        c?.trips?.length ? c.trips.length - 1 : -1, // newest trip by default
      ),
    );
  };

  const handleInvoiceSelect = (e) => {
    const id = e.target.value;
    const inv = (invoices || []).find((x) => x._id === id);
    if (!inv) {
      sf((p) => ({ ...p, invoice: id }));
      return;
    }
    sf((p) => ({
      ...p,
      invoice: id,
      clientName: inv.clientName || p.clientName,
      email: inv.email || p.email,
      phone: inv.phone || p.phone,
      destination: inv.destination || p.destination,
      currency: inv.currency || p.currency,
      country: inv.country || p.country,
      totalCost: inv.total != null ? String(inv.total) : p.totalCost,
      items: inv.items?.length
        ? inv.items.map((i) => ({
            description: i.description,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
          }))
        : p.items,
    }));
  };

  const handlePreFill = ({ totalCost, totalPaid, pendingBalance }) => {
    sf((p) => ({
      ...p,
      totalCost: totalCost.toFixed(2),
      amountPaid: totalPaid.toFixed(2),
      pendingBalance: pendingBalance.toFixed(2),
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.clientName.trim() || !f.email.trim()) {
      alert("Client name and email are required");
      return;
    }
    const { tripIndex, ...fields } = f; // tripIndex is only used by the form
    onSubmit({
      ...fields,
      receiptReference: f.receiptReference.trim() || undefined,
      country: f.country || undefined,
      invoice: f.invoice || undefined,
      client: f.client || undefined,
      items: f.items.filter((i) => i.description.trim()),
      bankPaymentInfo: Object.values(f.bankPaymentInfo).some(Boolean)
        ? f.bankPaymentInfo
        : undefined,
      paystackPaymentLink: f.paystackPaymentLink || undefined,
      transactionReference: f.transactionReference || undefined,
      totalCost: f.totalCost !== "" ? parseFloat(f.totalCost) : undefined,
      amountPaid: f.amountPaid !== "" ? parseFloat(f.amountPaid) : undefined,
      pendingBalance:
        f.pendingBalance !== "" ? parseFloat(f.pendingBalance) : undefined,
      dueDate: f.dueDate || undefined,
      paymentDate: f.paymentDate || undefined,
      tripStartDate: f.tripStartDate || undefined,
      tripEndDate: f.tripEndDate || undefined,
    });
  };

  const [tab, setTab] = useState("details");

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
        {["details", "payment", "items", "notes"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${tab === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── DETAILS TAB ───────────────────────────────────────────────────────── */}
      {tab === "details" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Link to Invoice */}
            {invoices?.length > 0 && (
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
                  Link to Invoice (optional)
                </label>
                <select
                  value={f.invoice}
                  onChange={handleInvoiceSelect}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
                >
                  <option value="">Select invoice to auto-fill details…</option>
                  {(invoices || []).map((inv) => (
                    <option key={inv._id} value={inv._id}>
                      {inv.invoiceReference} – {inv.clientName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Receipt Reference */}
            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
                Receipt Reference{" "}
                <span className="font-normal text-slate-400">
                  (auto-generated if blank)
                </span>
              </label>
              <input
                type="text"
                placeholder="RCT-2025-001 or leave blank"
                value={f.receiptReference}
                onChange={set("receiptReference")}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
              />
            </div>

            {/* Client selector */}
            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
                Client
              </label>
              <select
                value={f.client}
                onChange={handleClientSelect}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
              >
                <option value="">
                  Select client (optional — or type below)
                </option>
                {(clients || []).map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.fullName}
                  </option>
                ))}
              </select>
            </div>

            {/* Which trip — fills destination, trip dates, currency and total cost */}
            {selectedClient?.trips?.length > 0 && (
              <div className="col-span-2">
                <ClientTripSelect
                  client={selectedClient}
                  value={f.tripIndex}
                  onChange={(idx) => sf((p) => applyTrip(p, selectedClient, idx))}
                />
              </div>
            )}

            <Inp
              label="Client Name *"
              placeholder="Jane Doe"
              value={f.clientName}
              onChange={set("clientName")}
              required
            />
            <Inp
              label="Email *"
              type="email"
              placeholder="jane@example.com"
              value={f.email}
              onChange={set("email")}
              required
            />
            <Inp
              label="Phone"
              placeholder="+234..."
              value={f.phone}
              onChange={set("phone")}
            />

            {/* Country */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                Country
              </label>
              <select
                value={f.country}
                onChange={set("country")}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Destination — auto-populated from client, always editable */}
            <Inp
              label="Destination"
              placeholder="Dubai, Abu Dhabi"
              value={f.destination}
              onChange={set("destination")}
            />

            {/* Trip dates — calendar pickers */}
            <DateInp
              label="Trip Start Date"
              value={f.tripStartDate}
              onChange={set("tripStartDate")}
            />
            <DateInp
              label="Trip End Date"
              value={f.tripEndDate}
              onChange={set("tripEndDate")}
              min={f.tripStartDate}
            />

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                Currency *
              </label>
              <select
                value={f.currency}
                onChange={set("currency")}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <Sel
              label="Status"
              value={f.status}
              onChange={set("status")}
              options={[
                { value: "issued", label: "Issued" },
                { value: "cancelled", label: "Cancelled" },
                { value: "refunded", label: "Refunded" },
              ]}
            />
          </div>

          {/* Previous receipts from this client */}
          {f.client && (
            <div className="border-t border-slate-100 pt-4 space-y-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Client Receipt History
              </p>
              <ClientReceiptHistory
                clientId={f.client}
                currency={f.currency}
                onPreFill={handlePreFill}
              />
            </div>
          )}
        </div>
      )}

      {/* ── PAYMENT TAB ───────────────────────────────────────────────────────── */}
      {tab === "payment" && (
        <div className="space-y-4">
          {/* Payment flow card */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Payment Flow · {f.currency}
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Total Trip Cost */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Total Trip Cost
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={f.totalCost}
                  onChange={set("totalCost")}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right font-semibold"
                />
              </div>

              {/* Amount Paid (this receipt) */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Amount Paid
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={
                      itemsSubtotal > 0 ? itemsSubtotal.toFixed(2) : "0.00"
                    }
                    value={f.amountPaid}
                    onChange={set("amountPaid")}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right font-semibold"
                  />
                  {f.amountPaid === "" && itemsSubtotal > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setV("amountPaid", itemsSubtotal.toFixed(2))
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[rgb(var(--brand-rgb))] font-bold bg-[rgb(var(--brand-rgb)/0.1)] px-1.5 py-0.5 rounded-md"
                    >
                      Use subtotal
                    </button>
                  )}
                </div>
              </div>

              {/* Pending Balance — auto-calculated, still editable */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Pending Balance
                  <span className="font-normal text-slate-400 ml-1 normal-case">
                    (auto)
                  </span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={f.pendingBalance}
                  onChange={set("pendingBalance")}
                  className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-bold text-right outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]
                    ${
                      parseFloat(f.pendingBalance) > 0
                        ? "border-amber-300 bg-amber-50 text-amber-700"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                />
              </div>

              {/* Balance Due Date — calendar */}
              <DateInp
                label="Balance Due Date"
                value={f.dueDate}
                onChange={set("dueDate")}
              />
            </div>

            {/* Live summary pill */}
            {(parseFloat(f.totalCost) > 0 || parseFloat(f.amountPaid) > 0) && (
              <div className="flex items-center gap-3 pt-1 border-t border-slate-200 flex-wrap text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400">Total cost:</span>
                  <span className="font-bold text-slate-700">
                    {fmtMoney(parseFloat(f.totalCost) || 0, f.currency)}
                  </span>
                </div>
                <span className="text-slate-300">·</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400">Paid:</span>
                  <span className="font-bold text-emerald-600">
                    {fmtMoney(parseFloat(f.amountPaid) || 0, f.currency)}
                  </span>
                </div>
                <span className="text-slate-300">·</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400">Balance:</span>
                  <span
                    className={`font-bold ${parseFloat(f.pendingBalance) > 0 ? "text-amber-600" : "text-emerald-600"}`}
                  >
                    {fmtMoney(parseFloat(f.pendingBalance) || 0, f.currency)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Payment method details */}
          <Sel
            label="Payment Method"
            value={f.paymentMethod}
            onChange={set("paymentMethod")}
            options={PAYMENT_METHODS}
          />

          {/* Payment Date — calendar */}
          <DateInp
            label="Payment Date"
            value={f.paymentDate}
            onChange={set("paymentDate")}
          />

          <Inp
            label="Transaction Reference (optional)"
            placeholder="TXN-20250615-001"
            value={f.transactionReference}
            onChange={set("transactionReference")}
          />

          {/* Saved bank accounts / payment links — fills the fields below (still editable) */}
          <PaymentInfoPicker
            bank={f.bankPaymentInfo}
            link={f.paystackPaymentLink}
            currency={f.currency}
            onChange={({ bank, link }) =>
              sf((p) => ({ ...p, bankPaymentInfo: bank, paystackPaymentLink: link }))
            }
          />
          <Inp
            label="Paystack Payment Link (optional)"
            placeholder="https://paystack.com/pay/…"
            value={f.paystackPaymentLink}
            onChange={set("paystackPaymentLink")}
          />

          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider border-t border-slate-100 pt-3">
            Bank Account (for records — optional)
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Inp
              label="Account Number"
              placeholder="0123456789"
              value={f.bankPaymentInfo.accountNumber}
              onChange={setB("accountNumber")}
            />
            <Inp
              label="Account Name"
              placeholder={`${BRAND.name} Agency Ltd`}
              value={f.bankPaymentInfo.accountName}
              onChange={setB("accountName")}
            />
            <Inp
              label="Bank Name"
              placeholder="First Bank Nigeria"
              value={f.bankPaymentInfo.bankName}
              onChange={setB("bankName")}
              className="col-span-2"
            />
          </div>
        </div>
      )}

      {tab === "items" && (
        <ItemsEditor
          items={f.items}
          currency={f.currency}
          onChange={(items) => sf((p) => ({ ...p, items }))}
        />
      )}

      {tab === "notes" && (
        <Txa
          label="Notes"
          placeholder="e.g. Thank you for your payment. Have a wonderful trip!"
          value={f.notes}
          onChange={set("notes")}
          rows={6}
        />
      )}

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn
          type="submit"
          variant="primary"
          className="flex-1"
          loading={saving}
        >
          {initial._id ? "Update Receipt" : "Create Receipt"}
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </form>
  );
}

// ─── SEND MODAL ───────────────────────────────────────────────────────────────
function SendModal({ receipt, onSend, onClose, saving }) {
  const pendingLine =
    (receipt.pendingBalance || 0) > 0
      ? `\nPending Balance: ${fmtMoney(receipt.pendingBalance, receipt.currency)}${receipt.dueDate ? `\nBalance Due: ${fmtD(receipt.dueDate)}` : ""}`
      : "";
  const tripLine = receipt.tripStartDate
    ? `\nTrip Dates: ${fmtD(receipt.tripStartDate)}${receipt.tripEndDate ? ` – ${fmtD(receipt.tripEndDate)}` : ""}`
    : "";

  const [subject, setSubject] = useState(
    `Your Payment Receipt ${receipt.receiptReference} from ${BRAND.shortName}`,
  );
  const [body, setBody] = useState(
    `Dear ${receipt.clientName},\n\nThank you for your payment! Please find your receipt ${receipt.receiptReference} attached.\n\nAmount Paid: ${fmtMoney(receipt.amountPaid ?? receipt.total, receipt.currency)}${pendingLine}${receipt.destination ? `\nDestination: ${receipt.destination}` : ""}${tripLine}\n\nHave a wonderful trip!\n\nThank you for choosing ${BRAND.shortName}!\n\nWarm Regards,\n${BRAND.shortName} Customer Support Team`,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
          >
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700">Send Receipt</p>
          <p className="text-xs text-slate-400">To: {receipt.email}</p>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
            Subject
          </label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">
            Message Body
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] resize-none font-mono"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn
          variant="primary"
          className="flex-1"
          loading={saving}
          onClick={() => onSend({ subject, body })}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Send Receipt
        </Btn>
        <Btn variant="secondary" onClick={onClose}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

// ─── RECEIPT DETAIL SLIDE-OVER ────────────────────────────────────────────────
function ReceiptDetailPanel({
  receipt,
  onClose,
  onEdit,
  onStatusChange,
  onSend,
  actionSaving,
}) {
  const [printMode, setPrintMode] = useState(false);

  const handleDownload = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 300);
  };

  if (printMode) return <ReceiptPrintView invoice={receipt} />;

  const subtotal = (receipt.items || []).reduce(
    (s, i) => s + i.quantity * i.unitPrice,
    0,
  );
  const countryInfo = COUNTRIES.find((c) => c.value === receipt.country);
  const countryDisplay = countryInfo?.value
    ? countryInfo.label
    : receipt.country || null;
  const pmLabel =
    PAYMENT_METHODS.find((m) => m.value === receipt.paymentMethod)?.label ||
    receipt.paymentMethod;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
              Receipt
            </p>
            <p
              className="text-xl font-bold text-slate-800"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {receipt.receiptReference}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${RCT_STATUS_COLORS[receipt.status] || "bg-slate-100 text-slate-500"}`}
              >
                {receipt.status}
              </span>
              {pmLabel && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
                  {pmLabel}
                </span>
              )}
              {countryDisplay && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  {countryDisplay}
                </span>
              )}
              <span className="text-xs text-slate-400">
                {fmtD(receipt.paymentDate || receipt.createdAt)}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-600">
              {fmtMoney(
                receipt.amountPaid ?? receipt.total ?? subtotal,
                receipt.currency,
              )}
            </p>
            <p className="text-xs text-slate-400">
              {(receipt.items || []).length} item
              {receipt.items?.length !== 1 ? "s" : ""} ·{" "}
              {receipt.currency || "USD"}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-4 flex-wrap">
          {receipt.status === "issued" && (
            <Btn
              variant="secondary"
              size="sm"
              loading={actionSaving}
              onClick={() => onStatusChange("refunded")}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Mark Refunded
            </Btn>
          )}
          <Btn variant="secondary" size="sm" onClick={onSend}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Send
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onEdit}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </Btn>
          <Btn variant="ghost" size="sm" onClick={handleDownload}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            Download
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => window.print()}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm1-4h.01" />
            </svg>
            Print
          </Btn>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Client + Trip */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
              Client
            </p>
            <p className="font-bold text-slate-800">{receipt.clientName}</p>
            <p className="text-sm text-slate-500">{receipt.email}</p>
            {receipt.phone && (
              <p className="text-sm text-slate-500">{receipt.phone}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
              Trip
            </p>
            {receipt.destination && (
              <p className="text-sm font-semibold text-slate-700">
                {receipt.destination}
              </p>
            )}
            {(receipt.tripStartDate || receipt.tripEndDate) && (
              <p className="text-sm text-slate-500">
                {fmtD(receipt.tripStartDate)}
                {receipt.tripEndDate ? ` – ${fmtD(receipt.tripEndDate)}` : ""}
              </p>
            )}
            {countryDisplay && (
              <p className="text-sm text-slate-500 mt-0.5">{countryDisplay}</p>
            )}
          </div>
        </div>

        {/* Payment flow summary */}
        {(receipt.totalCost != null ||
          receipt.amountPaid != null ||
          receipt.pendingBalance != null) && (
          <div className="rounded-xl overflow-hidden border border-slate-100">
            <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Payment Summary
              </p>
              <span className="text-[10px] font-bold text-slate-400">
                {receipt.currency || "USD"}
              </span>
            </div>
            <div className="grid grid-cols-3 divide-x divide-slate-100">
              <div className="p-4">
                <p className="text-xs text-slate-400 mb-1">Total Trip Cost</p>
                <p className="text-base font-bold text-slate-800">
                  {fmtMoney(receipt.totalCost, receipt.currency)}
                </p>
              </div>
              <div className="p-4">
                <p className="text-xs text-slate-400 mb-1">Amount Paid</p>
                <p className="text-base font-bold text-emerald-600">
                  {fmtMoney(receipt.amountPaid, receipt.currency)}
                </p>
              </div>
              <div className="p-4">
                <p className="text-xs text-slate-400 mb-1">Pending Balance</p>
                <p
                  className={`text-base font-bold ${(receipt.pendingBalance || 0) > 0 ? "text-amber-600" : "text-emerald-600"}`}
                >
                  {fmtMoney(receipt.pendingBalance ?? 0, receipt.currency)}
                </p>
                {receipt.dueDate && (
                  <p className="text-[10px] text-slate-400 mt-1">
                    Due {fmtD(receipt.dueDate)}
                  </p>
                )}
              </div>
            </div>
            {receipt.paymentDate && (
              <div className="border-t border-slate-100 px-4 py-2.5 flex items-center gap-2 bg-white">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2"
                >
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-xs text-slate-500">
                  Payment received on{" "}
                  <strong>{fmtD(receipt.paymentDate)}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Payment method + transaction ref */}
        {(receipt.paymentMethod || receipt.transactionReference) && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">
              Payment Details
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {receipt.paymentMethod && (
                <div>
                  <p className="text-xs text-slate-400">Method</p>
                  <p className="font-semibold text-slate-700">{pmLabel}</p>
                </div>
              )}
              {receipt.transactionReference && (
                <div>
                  <p className="text-xs text-slate-400">Transaction Ref</p>
                  <p className="font-semibold text-slate-700 font-mono">
                    {receipt.transactionReference}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Line Items
            </p>
            {receipt.currency && receipt.currency !== BRAND.currency && (
              <span className="text-[10px] font-bold text-[rgb(var(--brand-rgb))] bg-[rgb(var(--brand-rgb)/0.1)] px-2 py-0.5 rounded-full">
                All amounts in {receipt.currency}
              </span>
            )}
          </div>
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              <span className="col-span-6">Description</span>
              <span className="col-span-2 text-center">Qty</span>
              <span className="col-span-2 text-right">Unit Price</span>
              <span className="col-span-2 text-right">Total</span>
            </div>
            {(receipt.items || []).map((item, i) => (
              <div
                key={i}
                className="grid grid-cols-12 px-3 py-3 border-t border-slate-50 hover:bg-slate-50 transition-colors items-start"
              >
                <span className="col-span-6 text-sm text-slate-700 whitespace-pre-line leading-relaxed pr-2">
                  {item.description}
                </span>
                <span className="col-span-2 text-sm text-slate-500 text-center">
                  {item.quantity}
                </span>
                <span className="col-span-2 text-sm text-slate-500 text-right">
                  {fmtMoney(item.unitPrice, receipt.currency)}
                </span>
                <span className="col-span-2 text-sm font-semibold text-slate-700 text-right">
                  {fmtMoney(item.quantity * item.unitPrice, receipt.currency)}
                </span>
              </div>
            ))}
            <div className="border-t border-slate-200 px-3 py-3 bg-slate-50 flex justify-end">
              <div className="space-y-1 w-48">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-semibold">
                    {fmtMoney(subtotal, receipt.currency)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1.5">
                  <span>Amount Paid</span>
                  <span className="text-emerald-600">
                    {fmtMoney(
                      receipt.amountPaid ?? receipt.total ?? subtotal,
                      receipt.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bank info */}
        {receipt.bankPaymentInfo?.accountNumber && (
          <div className="border border-slate-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
              Payment Received Via
            </p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-400">Account No.</p>
                <p className="font-semibold text-slate-700">
                  {receipt.bankPaymentInfo.accountNumber}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Account Name</p>
                <p className="font-semibold text-slate-700">
                  {receipt.bankPaymentInfo.accountName}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Bank</p>
                <p className="font-semibold text-slate-700">
                  {receipt.bankPaymentInfo.bankName}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {receipt.notes && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">
              Notes
            </p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {receipt.notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RECEIPTS TAB ─────────────────────────────────────────────────────────────
export function ReceiptsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: receipts,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch,
  } = usePaginated("/crm/receipts");
  const { data: clients } = usePaginated("/crm/clients", 1000);
  const { data: invoices } = usePaginated("/crm/invoices", 2000);
  const { data: summary, refetch: refetchSummary } = useAsync(() =>
    api.get("/crm/receipts/summary"),
  );

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [summaryCurrency, setSummaryCurrency] = useState(BRAND.currency);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionSaving, setAS] = useState(false);

  const shown = (receipts || []).filter((rct) => {
    const ms = filterStatus === "all" || rct.status === filterStatus;
    const mc = filterCountry === "all" || rct.country === filterCountry;
    const mq =
      !search ||
      rct.receiptReference?.toLowerCase().includes(search.toLowerCase()) ||
      rct.clientName?.toLowerCase().includes(search.toLowerCase());
    return ms && mc && mq;
  });

  const availableCountries = [
    ...new Set((receipts || []).map((r) => r.country).filter(Boolean)),
  ].sort();

  // ── Currency-specific summary (client-side, from loaded page of receipts) ─
  const byCur = (receipts || []).filter((r) => r.currency === summaryCurrency);
  const currencySummary = {
    issued: byCur.filter((r) => r.status === "issued").length,
    cancelled: byCur.filter((r) => r.status === "cancelled").length,
    refunded: byCur.filter((r) => r.status === "refunded").length,
    totalRevenue: byCur
      .filter((r) => r.status === "issued")
      .reduce((s, r) => s + (r.amountPaid ?? r.total ?? 0), 0),
    pendingTotal: byCur
      .filter((r) => r.status === "issued" && (r.pendingBalance || 0) > 0)
      .reduce((s, r) => s + (r.pendingBalance || 0), 0),
  };

  const doCreate = async (form) => {
    setSaving(true);
    try {
      await api.post("/crm/receipts", form);
      toast("Receipt created");
      setModal(null);
      refetch();
      refetchSummary();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (form) => {
    setSaving(true);
    try {
      await api.patch(`/crm/receipts/${sel._id}`, form);
      toast("Receipt updated");
      setModal(null);
      refetch();
      refetchSummary();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (
      !(await confirm({
        title: "Delete receipt?",
        message: "This receipt will be permanently removed.",
        confirmText: "Delete receipt",
      }))
    )
      return;
    try {
      await api.delete(`/crm/receipts/${id}`);
      toast("Deleted");
      refetch();
      refetchSummary();
      if (modal === "detail") setModal(null);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const doStatusChange = async (id, status) => {
    setAS(true);
    try {
      await api.patch(`/crm/receipts/${id}/status`, { status });
      toast(`Marked as ${status}`);
      refetch();
      refetchSummary();
      if (sel?._id === id) {
        const updated = await api.get(`/crm/receipts/${id}`);
        setSel(updated);
      }
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setAS(false);
    }
  };

  const doSend = async (id, dto) => {
    setSaving(true);
    try {
      await api.post(`/crm/receipts/${id}/send`, dto);
      toast("Receipt sent!");
      setModal("detail");
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (rct) => {
    try {
      const full = await api.get(`/crm/receipts/${rct._id}`);
      setSel(full);
      setModal("detail");
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
          Receipts
        </h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          New Receipt
        </Btn>
      </div>

      {/* ── Summary cards with USD / NGN toggle ── */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Summary in
          </span>
          <div className="flex gap-1">
            {SUMMARY_CURRENCIES.map((cur) => (
              <button
                key={cur}
                onClick={() => setSummaryCurrency(cur)}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all
                  ${summaryCurrency === cur ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              >
                {cur}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            {
              label: "Issued",
              val: currencySummary.issued,
              color: "bg-emerald-500",
            },
            {
              label: "Cancelled",
              val: currencySummary.cancelled,
              color: "bg-slate-400",
            },
            {
              label: "Refunded",
              val: currencySummary.refunded,
              color: "bg-amber-500",
            },
            {
              label: "Revenue",
              val: fmtMoney(currencySummary.totalRevenue, summaryCurrency),
              color: "bg-[rgb(var(--brand-rgb))]",
            },
            {
              label: "Pending",
              val: fmtMoney(currencySummary.pendingTotal, summaryCurrency),
              color: "bg-amber-400",
            },
          ].map((s) => (
            <Card key={s.label} className="p-4 flex items-center gap-3">
              <div
                className={`w-2.5 h-8 rounded-full flex-shrink-0 ${s.color}`}
              />
              <div>
                <p className="text-base font-bold text-slate-800 leading-tight">
                  {s.val}
                </p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  {s.label}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Filters ── */}
      <Card className="p-4 space-y-3">
        <div className="relative">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          >
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference or client name…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
          />
        </div>

        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Status
          </p>
          <div className="flex gap-2 flex-wrap">
            {RCT_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize
                  ${filterStatus === s ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {availableCountries.length > 0 && (
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
              Country
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterCountry("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filterCountry === "all" ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              >
                All Countries
              </button>
              {availableCountries.map((c) => {
                const info = COUNTRIES.find((x) => x.value === c);
                return (
                  <button
                    key={c}
                    onClick={() => setFilterCountry(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filterCountry === c ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                  >
                    {info ? info.label : c}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(filterStatus !== "all" || filterCountry !== "all" || search) && (
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <span className="text-[11px] text-slate-400">Filtering by:</span>
            {filterStatus !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))] capitalize">
                {filterStatus}
              </span>
            )}
            {filterCountry !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))]">
                {COUNTRIES.find((x) => x.value === filterCountry)?.label ||
                  filterCountry}
              </span>
            )}
            {search && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                "{search}"
              </span>
            )}
            <button
              onClick={() => {
                setFilterStatus("all");
                setFilterCountry("all");
                setSearch("");
              }}
              className="text-[11px] text-slate-400 hover:text-rose-500 font-bold transition-colors ml-1"
            >
              ✕ Clear
            </button>
          </div>
        )}
      </Card>

      {error && <ApiError msg={error} onRetry={refetch} />}
      {loading && <Spinner full />}

      {!loading && !error && (
        <>
          {!shown.length ? (
            <Empty
              icon="receipt"
              title="No receipts found"
              sub={
                search || filterStatus !== "all" || filterCountry !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first receipt"
              }
              action={
                !search &&
                filterStatus === "all" &&
                filterCountry === "all" && (
                  <Btn variant="primary" onClick={() => setModal("create")}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 4v16m8-8H4" />
                    </svg>
                    New Receipt
                  </Btn>
                )
              }
            />
          ) : (
            <div className="space-y-2">
              {shown.map((rct) => {
                const cInfo = COUNTRIES.find((x) => x.value === rct.country);
                const pmInfo = PAYMENT_METHODS.find(
                  (m) => m.value === rct.paymentMethod,
                );
                const hasPending = (rct.pendingBalance || 0) > 0;
                return (
                  <Card
                    key={rct._id}
                    className="p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          rct.status === "issued"
                            ? "bg-emerald-500"
                            : rct.status === "refunded"
                              ? "bg-amber-500"
                              : "bg-slate-300"
                        }`}
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-sm font-bold text-slate-800">
                            {rct.receiptReference}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${RCT_STATUS_COLORS[rct.status] || "bg-slate-100 text-slate-500"}`}
                          >
                            {rct.status}
                          </span>
                          {pmInfo && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600">
                              {pmInfo.label}
                            </span>
                          )}
                          {hasPending && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-600">
                              Bal: {fmtMoney(rct.pendingBalance, rct.currency)}
                            </span>
                          )}
                          {rct.country && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              {cInfo ? cInfo.label : rct.country}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {rct.clientName}
                          </span>
                          {rct.destination && <span>→ {rct.destination}</span>}
                          {rct.tripStartDate && (
                            <span>
                              {fmtD(rct.tripStartDate)}
                              {rct.tripEndDate
                                ? ` – ${fmtD(rct.tripEndDate)}`
                                : ""}
                            </span>
                          )}
                          {rct.transactionReference && (
                            <span className="font-mono">
                              {rct.transactionReference}
                            </span>
                          )}
                          {hasPending && rct.dueDate && (
                            <span className="text-amber-500 font-semibold flex items-center gap-1">
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Due {fmtD(rct.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right mr-2">
                        <p
                          className={`text-base font-bold ${
                            rct.status === "issued"
                              ? "text-emerald-600"
                              : rct.status === "refunded"
                                ? "text-amber-600"
                                : "text-slate-800"
                          }`}
                        >
                          {/* Amount paid on this receipt — same figure as the detail panel */}
                          {fmtMoney(rct.amountPaid ?? rct.total, rct.currency)}
                        </p>
                        {rct.totalCost != null &&
                          rct.totalCost !== (rct.amountPaid ?? rct.total) && (
                            <p className="text-[10px] text-slate-400">
                              of {fmtMoney(rct.totalCost, rct.currency)}
                            </p>
                          )}
                        <p className="text-[10px] text-slate-400">
                          {rct.currency || "USD"} · {(rct.items || []).length}{" "}
                          item{rct.items?.length !== 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => openDetail(rct)}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSel(rct);
                            setModal("edit");
                          }}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => doDelete(rct._id)}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <Pages
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </>
      )}

      {/* ── MODALS ── */}
      {modal === "create" && (
        <Modal title="New Receipt" onClose={() => setModal(null)} wide>
          <ReceiptForm
            clients={clients || []}
            invoices={invoices || []}
            onSubmit={doCreate}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}

      {modal === "edit" && sel && (
        <Modal
          title={`Edit ${sel.receiptReference}`}
          onClose={() => setModal(null)}
          wide
        >
          <ReceiptForm
            initial={sel}
            clients={clients || []}
            invoices={invoices || []}
            onSubmit={doEdit}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}

      {modal === "send" && sel && (
        <Modal title="Send Receipt" onClose={() => setModal("detail")}>
          <SendModal
            receipt={sel}
            saving={saving}
            onSend={(dto) => doSend(sel._id, dto)}
            onClose={() => setModal("detail")}
          />
        </Modal>
      )}

      {modal === "detail" && sel && (
        <div
          className="fixed inset-0 z-50 flex"
          style={{
            background: "rgba(15,23,42,.55)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="ml-auto w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl"
            style={{ animation: "slideIn .25s ease" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                Receipt Detail
              </span>
              <button
                onClick={() => setModal(null)}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ReceiptDetailPanel
                receipt={sel}
                actionSaving={actionSaving}
                onClose={() => setModal(null)}
                onEdit={() => setModal("edit")}
                onSend={() => setModal("send")}
                onStatusChange={(status) => doStatusChange(sel._id, status)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}